/* eslint-disable */

import { paramCase, pascalCase } from 'change-case';
import cloneDeep from 'lodash/cloneDeep.js';
import filter from 'lodash/filter.js';
import flatMap from 'lodash/flatMap.js';
import forEach from 'lodash/forEach.js';
import get from 'lodash/get.js';
import has from 'lodash/has.js';
import isEmpty from 'lodash/isEmpty.js';
import keys from 'lodash/keys.js';
import pluralize from 'pluralize';
import uuidV4 from 'uuid/v4.js';
import { FILE_UPLOAD_CONTEXT } from '@/constants/fileUploadRejection.js';
import { handleResponseError, localDateToUTC } from '@/Helpers.js';
import Activity from '@/models/Activity.js';
import ActivityTemplate from '@/models/ActivityTemplate.js';
import CourseSettings from '@/models/CourseSettings.js';
import CourseSettingsTemplate from '@/models/CourseSettingsTemplate.js';
import CourseTemplate from '@/models/CourseTemplate.js';
import Section from '@/models/Section.js';
import SectionTemplate from '@/models/SectionTemplate.js';
import axios from '@/plugins/axios.js';
import moment from '@/plugins/vue-moment.js';
import MenuService from '@/services/MenuService.js';
import toastService from '@/services/ToastService.js';
import { isReorderStructuralValidationError } from '@/store/utilities/ConsecutiveBreakValidation.js';
import CancelSource, { isAxiosCancel } from '@/utilities/CancelSource.js';
import { getPayloadTooLargeMessage } from '@/utilities/ErrorPackage.js';
import { omitAttendanceFields } from '@/utilities/omitAttendanceFields.js';
import RecursiveSearch from '@/utilities/RecursiveSearch.js';
import { userHasFeature } from '@/utilities/userHasFeature.js';
import { reportFileUploadServerError } from '@/utils/reportFileUploadRejection.js';

const getNamespace = function (type) {
    return pluralize(pascalCase(type));
};

/**
 * Convert a snake_case or dot.notation field name to a friendly Title Case label.
 * Examples:
 *   "due_at" → "Due At"
 *   "minimum_passing_score" → "Minimum Passing Score"
 *   "activity_settings.due_at" → "Due At"
 */
const formatFieldName = field => {
    // Take the last part if it's a nested field (e.g., "activity_settings.due_at" → "due_at")
    const fieldName = field.includes('.') ? field.split('.').pop() : field;

    // Convert snake_case to Title Case
    return fieldName
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

const refusedAttendanceScenario = error =>
    Object.keys(get(error, 'response.data.errors', {})).some(
        field => field.split('.').pop() === 'attendance_scenario_id'
    );

/**
 * Put the stored attendance scenario, and the flexible attendance a scenario pick can switch on, back on the form
 * after the server refuses a pick.
 */
const restoreStoredAttendanceScenario = async (context, course, isTemplate) => {
    const settingsKey = isTemplate ? 'course_settings_template' : 'course_settings';
    const rejected = course[settingsKey];

    if (!rejected?.id) {
        return;
    }

    let stored;

    try {
        const { data } = await axios.get(
            `/api/${isTemplate ? 'course-settings-templates' : 'course-settings'}/${rejected.id}`,
            { errorHandle: false }
        );
        stored = data.data;
    } catch {
        return;
    }

    const current = context.rootGetters['CourseTemplateForm/courseItem'](settingsKey) || {};

    if (
        current.id !== rejected.id ||
        (current.attendance_scenario_id ?? null) !== (rejected.attendance_scenario_id ?? null)
    ) {
        return;
    }

    const restored = { attendance_scenario_id: stored.attendance_scenario_id ?? null };

    if (
        has(stored, 'has_flexible_attendance') &&
        current.has_flexible_attendance === rejected.has_flexible_attendance
    ) {
        restored.has_flexible_attendance = stored.has_flexible_attendance;
    }

    context.commit(
        'CourseTemplateForm/updateCourseField',
        { field: settingsKey, value: { ...current, ...restored } },
        { root: true }
    );

    (isTemplate ? CourseSettingsTemplate : CourseSettings).update({ where: rejected.id, data: restored });
};

const parseTimeString = (time, timezone) => {
    const data = moment.tz(time.toLocaleString(), 'M/D/Y, hh:mm:ss A', timezone);

    return `${data.format('HH:mm')}`;
};

const checkWeekSettings = (payload, key) => {
    return ['week', 'day', 'time'].every(item => payload[`${item}_${key}`]);
};

const setToNull = (payload, key) => {
    ['week', 'day', 'time'].forEach(item => {
        payload[`${item}_${key}`] = null;
    });

    return payload;
};

export const ACTIVITY_SCHEDULE_FIELDS = [
    'week_due',
    'day_due',
    'time_due',
    'week_visible',
    'day_visible',
    'time_visible',
    'due_at',
    'visible_at',
];

const hasScheduleValue = value => value !== null && value !== undefined && value !== '';

export const activitySettingsNeedsOrderSync = settings =>
    !!settings && ACTIVITY_SCHEDULE_FIELDS.some(field => hasScheduleValue(settings[field]));

const SINGLE_ATTEMPT_ACTIVITY_TYPES = ['view-information', 'acknowledge-information'];

const getActivityType = activity => activity?.activity_type || activity?.activity_type_key || null;

const normalizeSingleAttemptSettings = (activity, settings) => {
    if (!settings || !SINGLE_ATTEMPT_ACTIVITY_TYPES.includes(getActivityType(activity))) {
        return settings;
    }

    settings.max_attempts_allowed = 1;

    return settings;
};

export default {
    state: {
        indicators: {
            // key: { message: '', saved: false, time: null }
        },
        errors: {
            // key: { error: exception, message: '' }
        },
        activeId: null,
        uploadingMedia: null,
    },

    getters: {
        isEmpty: state => isEmpty(state.indicators),

        allSaved: state => {
            let saved = Object.keys(state.indicators).length > 0;

            Object.keys(state.indicators).forEach(key => {
                if (!get(state.indicators[key], 'saved', false)) {
                    saved = false;
                }
            });

            return saved;
        },

        trackSaved: state => {
            return indicatorList => {
                let saved = indicatorList.length > 0;

                indicatorList.forEach(key => {
                    if (!get(state.indicators[key], 'saved', false)) {
                        saved = false;
                    }
                });

                return saved;
            };
        },

        /**
         * Extract validation errors from 422 responses with field names for better UX.
         * Returns an array of objects: { field, fieldLabel, message, formatted }
         */
        validationErrors: state =>
            filter(
                flatMap(Object.keys(state.errors), key => {
                    // Check HTTP status (response.status) not response body (response.data.code)
                    if (get(state.errors[key], 'response.status') === 422) {
                        const errors = get(state.errors[key], 'response.data.errors', {});
                        // Include field names in error messages for clearer user feedback
                        return Object.entries(errors).flatMap(([field, messages]) =>
                            messages.map(message => {
                                const fieldLabel = formatFieldName(field);
                                return {
                                    field,
                                    fieldLabel,
                                    message,
                                    // Format: "Field Name: Error message" for display
                                    formatted: `${fieldLabel}: ${message}`,
                                };
                            })
                        );
                    }
                })
            ),

        isSaving: state => type => {
            let isSaving;

            const filteredKeys = filter(keys(state.indicators), key => key.includes(type));

            forEach(filteredKeys, key => {
                if (has(state.indicators, key)) {
                    if (state.indicators[key].message === 'Saving...') {
                        isSaving = true;
                    }
                }
            });

            return Boolean(isSaving);
        },
        getActiveId: state => {
            return state.activeId;
        },
    },

    actions: {
        setSaved({ commit }, payload) {
            commit('SET_SAVED', {
                indicator: payload.type,
                message: 'Saved',
                id: payload.id,
            });
        },

        setSaving({ commit }, payload) {
            commit('CLEAR_ERRORS');

            commit('SET_SAVING', {
                indicator: payload.type,
                message: 'Saving...',
                id: payload.id,
            });
        },

        setError({ commit }, { indicator, error, id }) {
            error.message = error.message || `Action not saved, reference: ${indicator}`;

            commit('SET_ERROR', { indicator, error, id });
        },

        clearError({ commit }, indicator) {
            commit('CLEAR_ERROR', indicator);
        },

        clearErrors({ commit }) {
            commit('CLEAR_ERRORS');
        },

        async activities(context, { mutation, activity }) {
            const payload = cloneDeep(activity);
            const cancelScope =
                typeof mutation === 'string' ? `${mutation}:${payload.uuid}` : `${mutation.type}:${payload.uuid}`;
            const cancel = CancelSource(cancelScope);
            const method = payload.id ? 'put' : 'post';
            const temp = payload.type.includes('temp');
            const section_type = temp ? 'SectionTemplates' : 'Sections';
            const parent_type = temp ? 'section_template_id' : 'section_id';
            const activity_settings = temp ? 'activity_settings_template' : 'activity_settings';
            const course_id_type = temp ? 'course_template_id' : 'course_id';
            let hasSubmissions = false;

            // Fill since route requires a non empty settings object
            if (isEmpty(payload[activity_settings])) {
                payload[activity_settings] = {
                    max_attempts_allowed: 1,
                    allow_submissions_after_due_date: false,
                    due_at: null,
                };
            }

            // if activity type is discussion thread, and there is no id, assume it is being
            // created for the first time and by default set is_for_attendance to true
            if (!payload.id && payload.activity_type === 'discussion-thread') {
                payload[activity_settings].is_for_attendance = true;
            }

            // In some scenarios, the settings payload is present but not the max_attempts_allowed property, which is required
            if (!get(payload, `${activity_settings}.max_attempts_allowed`)) {
                payload[activity_settings].max_attempts_allowed = 1;
            }

            normalizeSingleAttemptSettings(payload, payload[activity_settings]);

            if (!payload[course_id_type]) {
                payload[course_id_type] = context.rootGetters['CourseTemplateForm/courseItem']('id');

                if (course_id_type === 'course_id') {
                    payload.activity_template_id = null;
                }
            }

            if (payload.parent && !payload[parent_type]) {
                payload[parent_type] = context.rootGetters[`${section_type}/${payload.parent}/get`]('id');
            }

            if (payload[activity_settings].week_due && !payload[activity_settings].day_due) {
                payload[activity_settings].week_due = null;
            }

            if (payload[activity_settings].week_visible && !payload[activity_settings].day_visible) {
                payload[activity_settings].week_visible = null;
            }

            if (payload[activity_settings].due_at) {
                payload[activity_settings].due_at = moment
                    .utc(payload[activity_settings].due_at)
                    .format('YYYY-MM-DD HH:mm:ss');
            }

            if (payload[activity_settings].visible_at) {
                payload[activity_settings].visible_at = moment
                    .utc(payload[activity_settings].visible_at)
                    .format('YYYY-MM-DD HH:mm:ss');
            }

            if (payload[activity_settings].time_due) {
                payload[activity_settings].time_due = parseTimeString(payload[activity_settings].time_due);
            }

            if (payload[activity_settings].time_visible) {
                payload[activity_settings].time_visible = parseTimeString(payload[activity_settings].time_visible);
            }

            if (activity.submissions) {
                hasSubmissions = activity.submissions.length > 0;
            }

            const activity_endpoint = pluralize(paramCase(payload.type));
            const resource = method === 'put' ? `/${payload.id}` : '';
            try {
                const response = await axios[method](
                    `/api/${activity_endpoint}${resource}?include=media&has_submissions=${hasSubmissions}`,
                    payload,
                    {
                        ...cancel.axiosConfig,
                        errorHandle: false,
                    }
                );

                const { data } = response.data;

                if (response) {
                    context.commit(
                        `${getNamespace(payload.type)}/${payload.uuid}/EDIT_FIELD`,
                        { field: 'media', value: data.media },
                        { root: true }
                    );

                    if (get(response, 'data.data.prerequisite_id')) {
                        context.commit(
                            `${getNamespace(payload.type)}/${payload.uuid}/EDIT_FIELD`,
                            { field: 'prerequisite_id', value: data.prerequisite_id },
                            { root: true }
                        );

                        context.commit(
                            `${getNamespace(payload.type)}/${payload.uuid}/EDIT_FIELD`,
                            { field: 'prerequisite', value: data.prerequisite },
                            { root: true }
                        );
                    }

                    if (temp) {
                        await ActivityTemplate.insert({ data: data });

                        await context.dispatch('Menu/setMenu', CourseTemplate.find(payload.course_template_id).menu(), {
                            root: true,
                        });
                    } else {
                        await Activity.insert({ data: data });
                    }
                }

                if (response && !payload.id) {
                    context.commit(
                        `${getNamespace(payload.type)}/${payload.uuid}/EDIT_FIELD`,
                        { field: 'id', value: data.id },
                        { root: true }
                    );

                    context.commit(
                        `${getNamespace(payload.type)}/${payload.uuid}/EDIT_FIELD`,
                        { field: activity_settings, value: data[activity_settings] },
                        { root: true }
                    );

                    if (payload.parent) {
                        await context.dispatch(
                            `${getNamespace(payload.type)}/${payload.uuid}/parseSettings`,
                            { item: payload },
                            { root: true }
                        );
                    }

                    // New activity persisted: clear "newly created" editing state.
                    // The server-derived fields the activity needs (id, settings, media,
                    // prerequisite, menu) are applied in place above. We deliberately do NOT
                    // re-fetch and re-hydrate the whole course here: that purged the store
                    // mid-edit and dropped any text typed while this first save was in flight.
                    await context.dispatch(
                        `${getNamespace(payload.type)}/updateItem`,
                        { uuid: payload.uuid, field: 'displayStatus', value: { editing: false } },
                        { root: true }
                    );
                }

                context.commit('UPLOADING_MEDIA', null);

                return response;
            } catch (error) {
                if (isAxiosCancel(error)) {
                    throw error;
                }

                // Only a failed request that carried new media (base64 items without an id) is a
                // failed upload. Errors thrown while applying a successful response are not.
                const uploadedMedia = Array.isArray(payload.media)
                    ? payload.media.filter(item => item && !item.id)
                    : [];

                if (error?.isAxiosError && uploadedMedia.length > 0) {
                    reportFileUploadServerError(
                        FILE_UPLOAD_CONTEXT.ACTIVITY_ATTACHMENT_INSTRUCTOR,
                        uploadedMedia,
                        error
                    );
                }

                context.commit('UPLOADING_MEDIA', null);

                // Media uploads ride this whole-activity PUT as base64 JSON; oversized
                // bodies are rejected by PHP/nginx as 413 before Laravel validation runs.
                if (error.response?.status === 413) {
                    error.message = getPayloadTooLargeMessage();
                }

                // Uploads optimistically append provisional base64 items (no media id) into
                // Vuex before this PUT completes. On failure, strip those phantoms so the
                // uploader's UI matches what is actually saved on the server.
                const namespace = getNamespace(payload.type);
                const getField = context.rootGetters[`${namespace}/${payload.uuid}/get`];
                const currentMedia = (typeof getField === 'function' ? getField('media') : null) ?? payload.media ?? [];

                if (Array.isArray(currentMedia) && currentMedia.some(item => !item?.id)) {
                    context.commit(
                        `${namespace}/${payload.uuid}/EDIT_FIELD`,
                        {
                            field: 'media',
                            value: currentMedia.filter(item => item?.id),
                        },
                        { root: true }
                    );
                }

                handleResponseError(error);
                await context.dispatch('setError', { indicator: mutation, error });
            }
        },

        async activitySettings(context, { mutation, activity }) {
            const timezone = context.rootGetters['InstitutionSettings/get']('timezone');

            const cancel = CancelSource(`${mutation}:${activity.uuid}`);
            const payload = cloneDeep(activity);

            const type = payload.type.includes('temp') ? '-templates' : '';

            let hasSubmissions = false;

            let settings = type.includes('temp') ? payload.activity_settings_template : payload.activity_settings;

            settings = normalizeSingleAttemptSettings(payload, settings);

            if (!checkWeekSettings(settings, 'due')) {
                settings = setToNull(settings, 'due');
            }

            if (!checkWeekSettings(settings, 'visible')) {
                settings = setToNull(settings, 'visible');
            }

            if (settings.due_at) {
                settings.due_at = moment(settings.due_at).utc().format('YYYY-MM-DD HH:mm:ss');
            }

            if (settings.visible_at) {
                settings.visible_at = moment(settings.visible_at).utc().format('YYYY-MM-DD HH:mm:ss');
            }

            if (activity.submissions) {
                hasSubmissions = activity.submissions.length > 0;
            }

            const apiRoute = `/api/activity-settings${type}/${settings.id}?has_submissions=${hasSubmissions}`;

            if (settings.time_due) {
                settings.time_due = parseTimeString(settings.time_due, timezone);
            }

            if (settings.time_visible) {
                settings.time_visible = parseTimeString(settings.time_visible, timezone);
            }

            try {
                await axios.put(apiRoute, settings, { ...cancel.axiosConfig, errorHandle: false });
            } catch (error) {
                if (isAxiosCancel(error)) {
                    throw error;
                }
                handleResponseError(error);
                await context.dispatch('setError', { indicator: mutation, error });
            }
        },

        async sections(context, { mutation, section }) {
            const payload = cloneDeep(section);
            const cancel = CancelSource(`${mutation}:${payload.uuid}`);
            const method = payload.id ? 'put' : 'post';
            const is_template = payload.type.includes('temp');
            const section_type = is_template ? 'SectionTemplates' : 'Sections';
            const section_settings = is_template ? 'section_settings_template' : 'section_settings';

            if (isEmpty(payload[section_settings])) {
                payload[section_settings] = {
                    visible_at: null,
                    type: 'topic',
                };
            }

            if (!payload.id) {
                const idType = is_template ? 'course_template_id' : 'course_id';

                payload[idType] = context.rootGetters['CourseTemplateForm/courseItem']('id');

                if (idType === 'course_id') {
                    payload.section_template_id = null;
                }
            }

            if (payload.parent) {
                payload.parent_id = context.rootGetters[`${section_type}/${payload.parent}/get`]('id');
            }

            if (!payload.parent) payload.parent_id = null;

            if (get(payload[section_settings], 'week_visible') && !get(payload[section_settings], 'day_visible')) {
                payload[section_settings].week_visible = null;
            }

            if (has(payload[section_settings], 'time_visible') && payload[section_settings].time_visible) {
                payload[section_settings].time_visible = parseTimeString(payload[section_settings].time_visible);
            }

            const sectionEndpoint = pluralize(paramCase(payload.type));
            const resource = method === 'put' ? `/${payload.id}` : '';

            payload.type = null;

            try {
                const { data } = await axios[method](`/api/${sectionEndpoint}${resource}`, payload, {
                    ...cancel.axiosConfig,
                    errorHandle: false,
                });

                if (get(data, 'data.prerequisite_id')) {
                    context.commit(
                        `${section_type}/${payload.uuid}/EDIT_FIELD`,
                        { field: 'prerequisite_id', value: data.data.prerequisite_id },
                        { root: true }
                    );

                    context.commit(
                        `${section_type}/${payload.uuid}/EDIT_FIELD`,
                        { field: 'prerequisite', value: data.data.prerequisite },
                        { root: true }
                    );
                }

                if (data && !payload.id) {
                    await context.dispatch(
                        `${section_type}/updateItem`,
                        { uuid: payload.uuid, field: 'id', value: data.data.id },
                        { root: true }
                    );

                    await context.dispatch(
                        `${section_type}/updateItem`,
                        { uuid: payload.uuid, field: section_settings, value: data.data[section_settings] },
                        { root: true }
                    );
                }

                context.commit(
                    `${section_type}/${payload.uuid}/EDIT_FIELD`,
                    { field: 'updateKey', value: uuidV4() },
                    { root: true }
                );

                await SectionTemplate.insert(data);

                if (is_template && CourseTemplate.find(payload.course_template_id)) {
                    await context.dispatch('Menu/setMenu', CourseTemplate.find(payload.course_template_id).menu(), {
                        root: true,
                    });
                }

                if (!is_template) {
                    const courseId =
                        get(data, 'data.course_id') || context.rootGetters['CourseTemplateForm/courseItem']('id');
                    if (courseId) {
                        try {
                            await MenuService.loadAndSetCourseMenu(context, courseId);
                        } catch (menuError) {
                            console.warn('Failed to refresh course menu after saving section:', menuError);
                        }
                    }
                }
            } catch (error) {
                if (isAxiosCancel(error)) {
                    throw error;
                }
                handleResponseError(error);
                await context.dispatch('setError', { indicator: mutation, error });
            }
        },

        async weeks(context, { sections }) {
            const isTemplate = sections.some(section => section.type.includes('temp'));
            const sectionType = isTemplate ? 'SectionTemplates' : 'Sections';
            const fieldType = isTemplate ? 'section_settings_template' : 'section_settings';

            const promises = [];

            sections.forEach(section => {
                const sectionEndpoint = pluralize(paramCase(section.type));

                const payload = cloneDeep(section);

                payload.type = null;

                promises.push(axios.post(`/api/${sectionEndpoint}`, payload, { errorHandle: false }));
            });

            await Promise.all(promises)
                .then(async payload => {
                    let courseId = null;

                    payload.forEach(resp => {
                        const section = resp.data.data;

                        if (!courseId && section.course_id) {
                            courseId = section.course_id;
                        }

                        context.dispatch(
                            `${sectionType}/updateItem`,
                            { uuid: section.uuid, field: 'id', value: section.id },
                            { root: true }
                        );

                        context.dispatch(
                            `${sectionType}/updateItem`,
                            { uuid: section.uuid, field: fieldType, value: section[fieldType] },
                            { root: true }
                        );

                        // Once a newly created week/break is persisted, remove the "newly created" styling
                        // by clearing its editing state so the UI no longer highlights it.
                        context.dispatch(
                            `${sectionType}/updateItem`,
                            { uuid: section.uuid, field: 'displayStatus', value: { editing: false } },
                            { root: true }
                        );
                    });

                    if (!isTemplate && courseId) {
                        try {
                            await MenuService.loadAndSetCourseMenu(context, courseId);
                        } catch (menuError) {
                            console.warn('Failed to refresh course menu after saving week/break:', menuError);
                        }
                    }
                })
                .catch(async error => {
                    if (isAxiosCancel(error)) {
                        return;
                    }

                    // Backend rejected a break due to a structural validation error (e.g. nesting).
                    // Remove any unsaved sections from the store so the UI stays in sync,
                    // then show a clear toast — the break was never persisted.
                    if (error?.response?.status === 422) {
                        const message = error?.response?.data?.message || 'This change could not be saved.';

                        sections.forEach(section => {
                            if (!section.id) {
                                const namespace = getNamespace(section.type);

                                context.dispatch(`${namespace}/remove`, section.uuid, { root: true });
                                context.commit(
                                    'CourseTemplateForm/deleteCourseItem',
                                    { uuid: section.uuid },
                                    { root: true }
                                );
                            }
                        });

                        toastService.open({
                            message,
                            type: 'is-danger',
                            position: 'is-bottom',
                            duration: 5000,
                        });

                        throw error;
                    }

                    await context.dispatch('setError', { indicator: 'SectionSettings/weeks', error });
                    handleResponseError(error);
                });
        },

        async courseForm(context, { mutation, course }) {
            const cancel = CancelSource(mutation);
            const type = course.id ? 'put' : 'post';

            // Determine if we're editing a course template based on the presence of course_settings_template
            // If course_settings_template exists, we're editing a template, otherwise we're editing a regular course
            const isTemplate = has(course, 'course_settings_template') && course.course_settings_template !== null;
            const courseType = isTemplate ? 'course-templates' : 'courses';

            if (!has(course, 'course_settings_template') && courseType !== 'courses') {
                course.course_settings_template = {};
            }

            if (courseType === 'courses' || courseType === 'course-templates') {
                delete course.items;
                delete course.activities;
                delete course.sections;
            }

            const attendEnabled = userHasFeature(context.rootState.Auth?.user, context.rootState.features?.ATTEND);

            if (attendEnabled && type === 'put' && courseType === 'courses' && course.course_settings) {
                course.course_settings = omitAttendanceFields(course.course_settings);
            }

            try {
                const data = await axios[type](`/api/${courseType}${course.id ? `/${course.id}` : ''}`, course, {
                    ...cancel.axiosConfig,
                    errorHandle: false,
                });

                if (!course.id && data) {
                    context.commit(
                        'CourseTemplateForm/updateCourseField',
                        {
                            field: 'id',
                            value: data.data.data.id,
                        },
                        { root: true }
                    );

                    course.id = data.data.data.id;
                }

                if (type === 'post' && courseType === 'course-templates') {
                    await CourseTemplate.insert({ data: data.data.data });
                }
            } catch (error) {
                if (isAxiosCancel(error)) {
                    throw error;
                }
                if (mutation === 'CourseTemplateForm/toggleCourseKey') {
                    if (courseType === 'course-templates' && course.course_settings_template.course_key_enabled) {
                        context.commit(
                            'CourseTemplateForm/updateCourseField',
                            {
                                field: 'course_settings_template.course_key_enabled',
                                value: false,
                            },
                            { root: true }
                        );
                    }

                    if (courseType === 'courses' && course.course_settings.course_key_enabled) {
                        context.commit(
                            'CourseTemplateForm/updateCourseField',
                            {
                                field: 'course_settings.course_key_enabled',
                                value: false,
                            },
                            { root: true }
                        );
                    }
                }

                if (mutation === 'CourseTemplateForm/setExxatCourseId') {
                    if (course.course_settings.exxat_course_id) {
                        context.commit(
                            'CourseTemplateForm/updateCourseField',
                            {
                                field: 'course_settings.exxat_course_id',
                                value: null,
                            },
                            { root: true }
                        );
                    }
                }

                handleResponseError(error);
                await context.dispatch('setError', { indicator: mutation, error });

                if (refusedAttendanceScenario(error)) {
                    await restoreStoredAttendanceScenario(context, course, isTemplate);
                }
            }
        },

        async updateOrder(context, { items, mutation }) {
            // Set saving state when called directly (not from autosaver callback)
            await context.dispatch('setSaving', mutation);

            const cancel = CancelSource(mutation);
            const search = new RecursiveSearch();
            const courseId = context.rootGetters['CourseTemplateForm/courseItem']('id');

            const temp = items.some(item => item.type.includes('temp'));

            const activities = temp ? 'activity_templates' : 'activities';
            const sections = temp ? 'section_templates' : 'sections';

            const section_settings = temp ? 'section_settings_template' : 'section_settings';
            const activity_settings = temp ? 'activity_settings_template' : 'activity_settings';

            const payload = {
                [activities]: [],
                [sections]: [],
            };

            const activity_namespace = temp ? 'ActivityTemplates' : 'Activities';
            const section_namespace = temp ? 'SectionTemplates' : 'Sections';

            await search.do(items, 'items', treeItem => {
                const activity = treeItem.type.includes('act');
                const section = treeItem.type.includes('sect');
                const section_id_prop = temp ? 'section_template_id' : 'section_id';

                if (activity) {
                    const item = context.rootGetters[`${activity_namespace}/get`](treeItem.uuid);

                    if (item.parent) {
                        item[section_id_prop] = context.rootGetters[`${section_namespace}/${item.parent}/get`]('id');
                    }

                    const activity = {
                        id: item.id,
                        [section_id_prop]: item.parent ? item[section_id_prop] : null,
                        order: treeItem.order,
                        data: item,
                    };

                    if (item[activity_settings].week_due && item[activity_settings].day_due) {
                        activity[activity_settings] = item[activity_settings];
                    }

                    if (item[activity_settings].due_at) {
                        activity[activity_settings] = item[activity_settings];
                    }

                    if (item[activity_settings].visible_at) {
                        activity[activity_settings] = item[activity_settings];
                    }

                    if (item.id) {
                        payload[activities].push(activity);
                    }
                }

                if (section) {
                    const item = context.rootGetters[`${section_namespace}/get`](treeItem.uuid);

                    if (item.parent) {
                        item.parent_id = context.rootGetters[`${section_namespace}/${item.parent}/get`]('id');
                    }

                    const section = {
                        id: item.id,
                        course_id: item.course_id,
                        parent_id: item.parent ? item.parent_id : null,
                        order: treeItem.order,
                    };

                    // Enforce root-level for Week/Break when updating order
                    try {
                        const currentType = get(item, `${section_settings}.type`);
                        if (currentType && ['week', 'break'].includes(currentType)) {
                            section.parent_id = null;
                        }
                    } catch (_e) {
                        // no-op
                    }

                    if (item[section_settings].type !== 'topic') {
                        // Structural order saves: omit week_start/week_end so the API skips
                        // week calendar validation; dates remain stored until explicitly edited.
                        const structuralSettings = { ...item[section_settings] };
                        delete structuralSettings.week_start;
                        delete structuralSettings.week_end;
                        section[section_settings] = structuralSettings;
                    }

                    if (item.id) {
                        payload[sections].push(section);
                    }
                }
            });

            if (!temp) {
                const courseSettings = context.rootGetters['CourseTemplateForm/courseItem']('course_settings');
                if (courseSettings?.end_date) {
                    payload.course_settings = {
                        end_date: courseSettings.end_date,
                    };
                }
            }

            if (!isEmpty(payload[activities]) || !isEmpty(payload[sections])) {
                try {
                    // The order endpoint only reads id / section_id / order from each activity.
                    // `data` carries the full activity model (with eager-loaded relations like
                    // discussion thread contentables) purely so updateActivitySettingsOnOrderChange
                    // can read it after the PUT — it must not go on the wire, where it bloats the
                    // body and can trip WAF content rules in fronted environments.
                    const wirePayload = {
                        ...payload,
                        [activities]: payload[activities].map(({ data: _omit, ...rest }) => rest),
                    };

                    const { data } = await axios.put(
                        `/api/${temp ? 'course-templates' : 'courses'}/${courseId}/order?include_sections=true`,
                        wirePayload,
                        {
                            ...cancel.axiosConfig,
                            errorHandle: false,
                        }
                    );

                    if (data?.data?.sections) {
                        await Section.insert({ data: data.data.sections });
                    }

                    await context.dispatch('updateActivitySettingsOnOrderChange', {
                        activities: payload[activities],
                        settings: activity_settings,
                    });

                    if (temp) {
                        await context.dispatch('Menu/setMenu', CourseTemplate.find(courseId).menu(), { root: true });
                    }

                    await context.dispatch('setSaved', mutation);
                } catch (error) {
                    // A canceled request means a newer reorder save superseded this one; the
                    // surviving request persists the order. Treat cancellation as benign at the
                    // source so no caller (drag handlers, syncWeeks, etc.) surfaces it as a failed
                    // reorder. This is the single place reorder persistence is performed.
                    if (isAxiosCancel(error)) {
                        return;
                    }

                    if (isReorderStructuralValidationError(error)) {
                        throw error;
                    }

                    await context.dispatch('setError', { indicator: mutation, error });
                    handleResponseError(error);
                }
            }
        },

        async rubrics(context, { rubric, mutation }) {
            await context.dispatch('setSaving', mutation.type);

            const cancel = CancelSource(mutation);

            const type = rubric.id ? 'put' : 'post';

            const template = mutation.type.toLowerCase().includes('temp') ? '-template' : '';

            const hasSubmissions = mutation.payload.has_submissions;

            const apiRoute = `/api/rubric${template}s${type === 'put' ? `/${rubric.id}` : ''}?has_submissions=${hasSubmissions}`;

            try {
                const data = await axios[type](apiRoute, rubric, { ...cancel.axiosConfig, errorHandle: false });

                await context.dispatch('setSaved', mutation.type);

                if (!data) {
                    return;
                }

                return data.data.data;
            } catch (error) {
                if (isAxiosCancel(error)) {
                    throw error;
                }
                handleResponseError(error);
                await context.dispatch('setError', { indicator: mutation, error });
            }
        },

        async rubricBanks(context, { rubricBank, mutation }) {
            if (mutation.payload.isDuplicate || mutation.payload.isOverLimit) {
                const error = { message: 'Duplicate Value' };
                await context.dispatch('setError', { indicator: mutation, error });

                return;
            }

            await context.dispatch('setSaving', mutation.type);

            const cancel = CancelSource(mutation);

            const type = rubricBank.id ? 'put' : 'post';

            const apiRoute = `/api/rubric-banks${type === 'put' ? `/${rubricBank.id}` : ''}`;

            try {
                const data = await axios[type](apiRoute, rubricBank, { ...cancel.axiosConfig, errorHandle: false });

                await context.dispatch('setSaved', mutation.type);

                if (!data) {
                    return;
                }

                return data.data.data;
            } catch (error) {
                if (isAxiosCancel(error)) {
                    throw error;
                }
                handleResponseError(error);
                await context.dispatch('setError', { indicator: mutation, error });
            }
        },

        async remove(context, { url }) {
            if (url.includes('null')) return;

            try {
                await axios.delete(url, { errorHandle: false });
            } catch (error) {
                await context.dispatch('setError', { indicator: 'CourseItem/remove', error });
                handleResponseError(error);

                return error;
            }
        },

        async updateActivitySettingsOnOrderChange(context, { activities, settings }) {
            const temp = settings.includes('temp');
            const apiRoute = temp ? 'activity-settings-templates' : 'activity-settings';
            const timezone = context.rootGetters['InstitutionSettings/get']('timezone');

            const updates = activities.reduce((acc, item) => {
                const source = item?.data;

                if (!source || !has(source, settings) || !activitySettingsNeedsOrderSync(source[settings])) {
                    return acc;
                }

                const activity = cloneDeep(source);

                if (temp) {
                    if (!checkWeekSettings(activity[settings], 'due')) {
                        activity[settings] = setToNull(activity[settings], 'due');
                    }

                    if (!checkWeekSettings(activity[settings], 'visible')) {
                        activity[settings] = setToNull(activity[settings], 'visible');
                    }

                    if (activity[settings].time_due) {
                        activity[settings].time_due = parseTimeString(activity[settings].time_due);
                    }

                    if (activity[settings].time_visible) {
                        activity[settings].time_visible = parseTimeString(activity[settings].time_visible);
                    }
                }

                if (!temp) {
                    if (activity[settings].due_at) {
                        activity[settings].due_at = localDateToUTC(activity[settings].due_at, timezone)
                            .utc()
                            .format('YYYY-MM-DD HH:mm:ss');
                    }

                    if (activity[settings].visible_at) {
                        activity[settings].visible_at = localDateToUTC(activity[settings].visible_at, timezone)
                            .utc()
                            .format('YYYY-MM-DD HH:mm:ss');
                    }
                }

                acc.push(activity[settings]);

                return acc;
            }, []);

            if (!updates.length) {
                return;
            }

            try {
                await axios.put(`/api/${apiRoute}-bulk`, updates, { errorHandle: false });
            } catch (error) {
                if (isAxiosCancel(error)) {
                    return;
                }

                if (error.response?.status === 413) {
                    console.error(
                        'Activity settings sync after reorder skipped (payload too large; order already saved):',
                        error
                    );

                    return;
                }

                await context.dispatch('setError', { indicator: 'ActivitySettings/updateActivitySettings', error });
                handleResponseError(error);
            }
        },
    },

    mutations: {
        SET_SAVED(state, { indicator, message, id }) {
            state.indicators[indicator] = {
                message,
                saved: true,
                time: moment(),
            };
            state.activeId = id;
        },

        SET_SAVING(state, { indicator, message, id }) {
            state.indicators[indicator] = {
                message,
                saved: false,
                time: null,
                id: id,
            };
        },

        SET_ERROR(state, { indicator, error, id }) {
            state.errors[indicator] = error;
            state.activeId = id;
        },

        CLEAR_ERROR(state, indicator) {
            delete state.errors[indicator];
        },

        CLEAR_ERRORS(state) {
            state.errors = {};
        },

        UPLOADING_MEDIA(state, uuid) {
            state.uploadingMedia = uuid;
        },
    },
};
